import { Button } from '@factorialco/deprecated-design-system'
import {
  AtsEditQuestionTemplateDocument,
  AtsQuestionOptionInput,
  AtsQuestionTemplatesListDocument,
} from 'generated/resources/graphql'
import { AtsQuestionTemplateFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditQuestion } from 'modules/ats/components/Questions/EditQuestion'
import { questionTypeHasOptions } from 'modules/ats/lib/utils/customQuestion'
import { validateQuestions } from 'modules/ats/lib/utils/questions_validations'

interface FormValues {
  label: string
  mandatory: boolean
  autoDisqualify: boolean
  options: AtsQuestionOptionInput[]
}

interface Props {
  questionTemplate: AtsQuestionTemplateFragment
  onClose: () => void
}

export const EditQuestionTemplate = ({ questionTemplate, onClose }: Props) => {
  const [update, { loading }] = useMutation(AtsEditQuestionTemplateDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsQuestionTemplate,
    refetchQueries: [AtsQuestionTemplatesListDocument],
  })

  const form = useForm<FormValues>({
    entity: questionTemplate,
    defaultValues: {
      label: '',
      mandatory: false,
      autoDisqualify: false,
      options: questionTemplate.options || [],
    },
    validate(data) {
      validateQuestions(data, {})
    },
    async onSubmit(values, { setErrors }) {
      const shouldSendOptions = questionTypeHasOptions(questionTemplate.questionType)

      const result = await update({
        variables: {
          id: questionTemplate.id,
          label: values.label,
          mandatory: values.mandatory,
          autoDisqualify: values.autoDisqualify,
          options: shouldSendOptions
            ? form.values.options?.map((option) => ({
                text: option.text,
                disqualifies: option.disqualifies,
              }))
            : null,
        },
      })

      const handled = handleResult(result, ({ label, mandatory }) => {
        setErrors({
          label,
          mandatory,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  const hasOptions = questionTypeHasOptions(questionTemplate.questionType)

  return (
    <Modal
      illustration="server-edit"
      title={i18n.t('ats.job_posting_settings.application_form.custom_questions.edit_modal_title')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button loading={loading} submit>
          {i18n.t('common.save')}
        </Button>
      }
    >
      <EditQuestion
        form={form}
        label={questionTemplate.label}
        questionType={questionTemplate.questionType}
        hasOptions={hasOptions}
        mandatory={questionTemplate.mandatory}
        template
        canSaveTemplates={false}
        saveAsTemplate={false}
        setSaveAsTemplate={() => {}}
      />
    </Modal>
  )
}
